/**
 * Debrief Re-evaluations Audit — admin view.
 *
 * Two-level privacy design (Apr 16, 2026 revision):
 *   • List view (default)   — classifier metadata only. No user chat content.
 *   • Detail view (on demand) — full verdict + surrounding chat turns,
 *     fetched from the audit-logged `/api/admin/debrief-reevaluations/{id}`
 *     endpoint. Each click writes an entry to admin_audit_log with
 *     target_user_id.
 *
 * The UI makes the boundary visible: expanding a row fetches the detail
 * endpoint (after a one-click confirmation surface) so there's no accidental
 * browsing of user chat content.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { storage } from '../services/storage';
import { API_BASE_URL } from '../utils/api';

// ── Types ───────────────────────────────────────────────────────────────────

type VerdictAction =
  | 'continue'
  | 'close_early'
  | 'plan_pivot'
  | 'health_pivot'
  | 'close_with_handoff';

interface RecalibrationHint {
  scope?: string | null;
  severity?: string | null;
  topic?: string | null;
}

// List endpoint returns metadata only (no user chat content).
interface VerdictListRow {
  id: string;
  user_id: string;
  conversation_id: string;
  activity_id: string | null;
  turn_index: number | null;
  action: VerdictAction;
  reason: string;
  recalibration_hint: RecalibrationHint | null;
  model_used: string;
  created_at: string;
}

interface ListResponse {
  verdicts: VerdictListRow[];
  total: number;
  counts_by_action: Record<VerdictAction, number>;
  limit: number;
  offset: number;
  period_days: number;
}

// Detail endpoint adds the gated content.
interface SurroundingTurn {
  id: string;
  role: 'user' | 'assistant' | string;
  content: string;
  created_at: string;
}

interface VerdictDetail extends VerdictListRow {
  user_message_excerpt: string;
  surrounding_turns: SurroundingTurn[];
}

const ACTIONS: VerdictAction[] = [
  'continue',
  'close_early',
  'plan_pivot',
  'health_pivot',
  'close_with_handoff',
];

// Action → color mapping. Literal class names so Tailwind JIT picks them up.
const ACTION_COLORS: Record<
  VerdictAction,
  { bg: string; chip: string; label: string }
> = {
  continue: { bg: 'bg-slate-400', chip: 'bg-slate-100 text-slate-700', label: 'Continue' },
  close_early: { bg: 'bg-gray-500', chip: 'bg-gray-100 text-gray-700', label: 'Close early' },
  plan_pivot: { bg: 'bg-orange-500', chip: 'bg-orange-100 text-orange-800', label: 'Plan pivot' },
  health_pivot: { bg: 'bg-red-500', chip: 'bg-red-100 text-red-800', label: 'Health pivot' },
  close_with_handoff: { bg: 'bg-blue-500', chip: 'bg-blue-100 text-blue-800', label: 'Off-topic handoff' },
};

const PAGE_SIZE = 50;

// ── API helper ──────────────────────────────────────────────────────────────

async function adminFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await storage.getItem('admin_token');
  const base =
    typeof window !== 'undefined' && window.location.hostname === 'localhost'
      ? ''
      : API_BASE_URL;
  const res = await fetch(`${base}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
    credentials: 'include',
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      detail = body.detail || body.message || detail;
    } catch {
      // fall through
    }
    throw new Error(detail);
  }
  return res;
}

// Component state for an expanded verdict row — tracks the fetch lifecycle
// so the UI can show "awaiting consent" / "loading" / loaded / error.
type DetailState =
  | { phase: 'awaiting_consent' }
  | { phase: 'loading' }
  | { phase: 'loaded'; detail: VerdictDetail }
  | { phase: 'error'; message: string };

// ── Component ───────────────────────────────────────────────────────────────

export const AdminDebriefsAudit: React.FC = () => {
  const [days, setDays] = useState<number>(7);
  const [actionFilter, setActionFilter] = useState<VerdictAction | ''>('');
  const [userIdFilter, setUserIdFilter] = useState<string>('');
  const [debouncedUserId, setDebouncedUserId] = useState<string>('');
  const [offset, setOffset] = useState<number>(0);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ListResponse | null>(null);

  // One row can be expanded at a time. Keyed by verdict id → DetailState.
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detailByVerdict, setDetailByVerdict] = useState<
    Record<string, DetailState>
  >({});

  // Debounce the user ID text input so we don't fire a request per keystroke.
  useEffect(() => {
    const h = setTimeout(() => setDebouncedUserId(userIdFilter.trim()), 300);
    return () => clearTimeout(h);
  }, [userIdFilter]);

  // Reset pagination + collapse expansions when any filter changes.
  useEffect(() => {
    setOffset(0);
    setExpandedId(null);
    setDetailByVerdict({});
  }, [days, actionFilter, debouncedUserId]);

  const loadVerdicts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      qs.set('days', String(days));
      qs.set('limit', String(PAGE_SIZE));
      qs.set('offset', String(offset));
      if (actionFilter) qs.set('action', actionFilter);
      if (debouncedUserId) qs.set('user_id', debouncedUserId);
      const res = await adminFetch(`/api/admin/debrief-reevaluations?${qs}`);
      const body = (await res.json()) as ListResponse;
      setData(body);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [days, actionFilter, debouncedUserId, offset]);

  useEffect(() => {
    loadVerdicts();
  }, [loadVerdicts]);

  const loadDetail = useCallback(async (verdictId: string) => {
    setDetailByVerdict((prev) => ({
      ...prev,
      [verdictId]: { phase: 'loading' },
    }));
    try {
      const res = await adminFetch(
        `/api/admin/debrief-reevaluations/${verdictId}`,
      );
      const detail = (await res.json()) as VerdictDetail;
      setDetailByVerdict((prev) => ({
        ...prev,
        [verdictId]: { phase: 'loaded', detail },
      }));
    } catch (e) {
      setDetailByVerdict((prev) => ({
        ...prev,
        [verdictId]: {
          phase: 'error',
          message: e instanceof Error ? e.message : String(e),
        },
      }));
    }
  }, []);

  const toggleExpand = useCallback(
    (verdictId: string) => {
      if (expandedId === verdictId) {
        setExpandedId(null);
        return;
      }
      setExpandedId(verdictId);
      // Initialize the row to "awaiting_consent" — no fetch until admin
      // explicitly clicks "Reveal chat content".
      setDetailByVerdict((prev) => ({
        ...prev,
        [verdictId]:
          prev[verdictId]?.phase === 'loaded'
            ? prev[verdictId]
            : { phase: 'awaiting_consent' },
      }));
    },
    [expandedId],
  );

  // Bar chart maxima (denominator for relative widths).
  const maxCount = useMemo(() => {
    if (!data) return 0;
    return Math.max(1, ...Object.values(data.counts_by_action));
  }, [data]);

  const canPrev = offset > 0;
  const canNext = data ? offset + PAGE_SIZE < data.total : false;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-semibold mb-1">Debrief re-evaluations</h1>
      <p className="text-sm text-slate-600 mb-4">
        Mid-debrief classifier verdicts. Use this to tune the 0.70 confidence
        threshold and catch prompt regressions.
      </p>

      {/* ── Privacy notice ─────────────────────────────────────────────── */}
      <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded p-3 mb-6 text-sm">
        <strong>Privacy:</strong> this view shows classifier metadata only.
        Expanding a row and clicking <em>Reveal chat content</em> fetches the
        user's message and Cedar's response — that action is logged to{' '}
        <code className="text-xs bg-amber-100 px-1 rounded">admin_audit_log</code>{' '}
        with the user ID. Only open chat content when you have a concrete
        tuning or incident reason.
      </div>

      {/* ── Filters ────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-3 items-end mb-6">
        <div>
          <label className="block text-xs text-slate-600 mb-1">Window</label>
          <select
            className="border border-slate-300 rounded px-2 py-1 text-sm"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            <option value={1}>Last 24h</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-slate-600 mb-1">Action</label>
          <select
            className="border border-slate-300 rounded px-2 py-1 text-sm"
            value={actionFilter}
            onChange={(e) =>
              setActionFilter(e.target.value as VerdictAction | '')
            }
          >
            <option value="">All actions</option>
            {ACTIONS.map((a) => (
              <option key={a} value={a}>
                {ACTION_COLORS[a].label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs text-slate-600 mb-1">User ID</label>
          <input
            type="text"
            placeholder="filter by user UUID…"
            className="w-full border border-slate-300 rounded px-2 py-1 text-sm font-mono"
            value={userIdFilter}
            onChange={(e) => setUserIdFilter(e.target.value)}
          />
        </div>
        <button
          className="border border-slate-300 rounded px-3 py-1 text-sm hover:bg-slate-50"
          onClick={() => loadVerdicts()}
          disabled={loading}
        >
          Refresh
        </button>
      </div>

      {/* ── Counts by action ──────────────────────────────────────────── */}
      <div className="border border-slate-200 rounded-lg p-4 mb-6 bg-white">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">
          Counts by action {data ? `(total: ${data.total})` : ''}
        </h2>
        {!data ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : (
          <div className="space-y-2">
            {ACTIONS.map((a) => {
              const n = data.counts_by_action[a] ?? 0;
              const widthPct = Math.round((n / maxCount) * 100);
              return (
                <div key={a} className="flex items-center gap-3">
                  <div className="w-40 text-xs text-slate-600">
                    {ACTION_COLORS[a].label}
                  </div>
                  <div className="flex-1 bg-slate-100 rounded h-6 relative overflow-hidden">
                    <div
                      className={`${ACTION_COLORS[a].bg} h-6 transition-all`}
                      style={{ width: `${widthPct}%` }}
                    />
                  </div>
                  <div className="w-16 text-right text-sm font-mono text-slate-700">
                    {n}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Table ──────────────────────────────────────────────────────── */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 mb-4 text-sm">
          Failed to load verdicts: {error}
        </div>
      )}

      <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-xs uppercase text-slate-600">
              <th className="px-3 py-2 w-44">Timestamp</th>
              <th className="px-3 py-2 w-32">Action</th>
              <th className="px-3 py-2 w-24">User</th>
              <th className="px-3 py-2 w-24">Model</th>
              <th className="px-3 py-2">Classifier reason</th>
              <th className="px-3 py-2 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {loading && !data ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            ) : data && data.verdicts.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-slate-500">
                  No verdicts in this window.
                </td>
              </tr>
            ) : (
              data?.verdicts.map((v) => {
                const colors = ACTION_COLORS[v.action];
                const isExpanded = expandedId === v.id;
                const detailState = detailByVerdict[v.id];
                return (
                  <React.Fragment key={v.id}>
                    <tr
                      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                      onClick={() => toggleExpand(v.id)}
                    >
                      <td className="px-3 py-2 text-xs text-slate-600 font-mono">
                        {new Date(v.created_at).toLocaleString()}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${colors.chip}`}
                        >
                          {colors.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-600">
                        {v.user_id.slice(0, 8)}…
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600 font-mono">
                        {v.model_used}
                      </td>
                      <td className="px-3 py-2 text-slate-700 text-xs">
                        {v.reason?.slice(0, 160)}
                      </td>
                      <td className="px-3 py-2 text-right text-xs text-slate-500">
                        {isExpanded ? 'hide ▴' : 'details ▾'}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <td colSpan={6} className="px-3 py-4">
                          <ExpandedDetail
                            verdict={v}
                            detailState={detailState}
                            onReveal={() => loadDetail(v.id)}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {data && (
          <div className="flex items-center justify-between px-3 py-2 border-t border-slate-200 bg-slate-50 text-sm">
            <div className="text-slate-600">
              Showing {data.verdicts.length === 0 ? 0 : offset + 1}–
              {offset + data.verdicts.length} of {data.total}
            </div>
            <div className="flex gap-2">
              <button
                className="border border-slate-300 rounded px-3 py-1 text-xs hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                disabled={!canPrev || loading}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                ← Prev
              </button>
              <button
                className="border border-slate-300 rounded px-3 py-1 text-xs hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                disabled={!canNext || loading}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── ExpandedDetail sub-component ────────────────────────────────────────────

interface ExpandedDetailProps {
  verdict: VerdictListRow;
  detailState: DetailState | undefined;
  onReveal: () => void;
}

const ExpandedDetail: React.FC<ExpandedDetailProps> = ({
  verdict,
  detailState,
  onReveal,
}) => {
  // Metadata is always visible — no user chat content yet.
  const meta = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      <div>
        <h3 className="text-xs uppercase text-slate-500 mb-1">
          Classifier reason (full)
        </h3>
        <p className="text-sm text-slate-800">{verdict.reason}</p>
      </div>
      <div>
        <h3 className="text-xs uppercase text-slate-500 mb-1">Metadata</h3>
        <div className="text-xs text-slate-600 space-y-1">
          <div>
            Model: <span className="font-mono">{verdict.model_used}</span>
          </div>
          {verdict.turn_index !== null && (
            <div>Turn index: {verdict.turn_index}</div>
          )}
          <div>
            Conversation:{' '}
            <span className="font-mono">{verdict.conversation_id}</span>
          </div>
          {verdict.activity_id && (
            <div>
              Activity: <span className="font-mono">{verdict.activity_id}</span>
            </div>
          )}
          <div>
            User: <span className="font-mono">{verdict.user_id}</span>
          </div>
        </div>
        {verdict.recalibration_hint && (
          <>
            <h3 className="text-xs uppercase text-slate-500 mt-3 mb-1">
              Recalibration hint
            </h3>
            <pre className="bg-white border border-slate-200 rounded p-2 text-xs font-mono overflow-x-auto">
              {JSON.stringify(verdict.recalibration_hint, null, 2)}
            </pre>
          </>
        )}
      </div>
    </div>
  );

  // Chat content area — gated on explicit reveal.
  let contentArea: React.ReactNode;
  switch (detailState?.phase) {
    case 'loaded':
      contentArea = <LoadedChatContent detail={detailState.detail} />;
      break;
    case 'loading':
      contentArea = (
        <p className="text-sm text-slate-500">
          Fetching chat content (will be audit-logged)…
        </p>
      );
      break;
    case 'error':
      contentArea = (
        <p className="text-sm text-red-700">
          Could not load detail: {detailState.message}
        </p>
      );
      break;
    case 'awaiting_consent':
    default:
      contentArea = (
        <div className="border border-slate-200 rounded bg-white p-4">
          <p className="text-sm text-slate-700 mb-3">
            <strong>Chat content is hidden.</strong> Click below to load the
            user's message and Cedar's response. This action is recorded in{' '}
            <code className="text-xs bg-slate-100 px-1 rounded">
              admin_audit_log
            </code>{' '}
            with the user ID.
          </p>
          <button
            onClick={onReveal}
            className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium rounded px-3 py-1.5"
          >
            Reveal chat content (audit-logged)
          </button>
        </div>
      );
  }

  return (
    <>
      {meta}
      <h3 className="text-xs uppercase text-slate-500 mb-2">Chat content</h3>
      {contentArea}
    </>
  );
};

const LoadedChatContent: React.FC<{ detail: VerdictDetail }> = ({ detail }) => (
  <div className="space-y-3">
    <div className="bg-white border border-slate-200 rounded p-3">
      <div className="text-xs uppercase text-slate-500 mb-1">
        User's message (what the classifier saw)
      </div>
      <div className="text-sm text-slate-800 whitespace-pre-wrap">
        {detail.user_message_excerpt}
      </div>
    </div>
    {detail.surrounding_turns.length > 0 && (
      <div>
        <div className="text-xs uppercase text-slate-500 mb-1">
          Surrounding turns ({detail.surrounding_turns.length})
        </div>
        <div className="space-y-2">
          {detail.surrounding_turns.map((t) => (
            <div
              key={t.id}
              className={
                t.role === 'assistant'
                  ? 'bg-blue-50 border border-blue-100 rounded p-2'
                  : 'bg-white border border-slate-200 rounded p-2'
              }
            >
              <div className="text-xs text-slate-500 mb-1 font-mono">
                {t.role === 'assistant' ? 'Cedar' : 'User'} •{' '}
                {new Date(t.created_at).toLocaleString()}
              </div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap">
                {t.content}
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

export default AdminDebriefsAudit;
